import type { DocumentRowMapped } from "./types";
import * as pmRepo from "@/stores/pm/repository";

/**
 * WHA-3270 — batch-hydrate `linked_issues` on a list of already-mapped
 * documents. Called by `listDocuments`, `fetchProjectDocuments`, and the
 * timeline fetchers right after the main query so the row renderers
 * can show the `LinkedIssuesChip` without an extra round-trip per row.
 *
 * Mutates `docs` in place. Skips the call entirely on empty input.
 */
export async function hydrateLinkedIssues(docs: DocumentRowMapped[]): Promise<void> {
  if (docs.length === 0) return;
  try {
    const ids = docs.map((d) => d.id);
    const byDoc = await pmRepo.getIssuesForDocuments(ids);
    for (const doc of docs) {
      const issues = byDoc.get(doc.id);
      if (issues && issues.length > 0) {
        doc.linked_issues = issues;
      }
    }
  } catch (err) {
    // Surfacing this would break the entire docs page over a chip; log
    // and degrade gracefully — empty `linked_issues` just hides the chip.
    console.error("hydrateLinkedIssues failed:", err);
  }
}

/**
 * Join an embedded `user_profiles` row into a display name, or `null` when the
 * embed is absent/empty. Shared by every `*_by_name` field so a verifier and an
 * uploader are rendered identically.
 */
export function profileDisplayName(
  profile: { first_name?: string | null; last_name?: string | null } | null | undefined
): string | null {
  if (!profile) return null;
  const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim();
  return name.length > 0 ? name : null;
}

/**
 * Map raw Supabase document row to typed DocumentRowMapped.
 * Handles joining user_profiles for created_by_name and
 * M2M relations for categories, design_disciplines, and design_stages.
 */
export function mapDocumentRow(doc: any): DocumentRowMapped {
  const categories =
    doc.document_document_categories?.map((rel: any) => ({
      id: rel.document_categories.id,
      name: rel.document_categories.category_name,
      name_et: rel.document_categories.name_et ?? null,
    })) ?? [];

  const design_disciplines =
    doc.document_design_disciplines?.map((rel: any) => ({
      id: rel.design_disciplines.id,
      name: rel.design_disciplines.name,
      name_en: rel.design_disciplines.name_en ?? null,
      abbreviation: rel.design_disciplines.abbreviation,
      description: rel.design_disciplines.description,
    })) ?? [];

  const design_stages =
    doc.document_design_stages?.map((rel: any) => ({
      id: rel.design_stages.id,
      name: rel.design_stages.name,
      name_en: rel.design_stages.name_en ?? null,
      abbreviation: rel.design_stages.abbreviation,
    })) ?? [];

  return {
    id: doc.id,
    filename: doc.filename,
    file_path: doc.file_path,
    building_id: doc.building_id,
    parent_document_id: doc.parent_document_id,
    content_type: doc.content_type,
    size: doc.size,
    created_at: doc.created_at,
    original_created_at: doc.original_created_at,
    modified_at: doc.modified_at,
    document_content_modified_date: doc.document_content_modified_date,
    effective_modified_at: doc.effective_modified_at ?? null,
    created_by: doc.created_by,
    created_by_name: profileDisplayName(doc.user_profiles),
    created_by_avatar_url: doc.user_profiles?.avatar_url ?? null,
    created_by_color: doc.user_profiles?.color ?? null,
    state: doc.state,
    version: doc.version,
    version_group: doc.version_group,
    source: doc.source ?? null,
    // `null` unless the query selected `is_latest_version` (only
    // `fetchSingleDocument` does); the store uses it to evict superseded rows.
    is_latest_version: doc.is_latest_version ?? null,
    container_type: doc.container_type ?? null,
    project_id: doc.project_id,
    folder_id: doc.folder_id,
    is_deleted: doc.is_deleted,
    approved: doc.approved ?? false,
    verified_at: doc.verified_at ?? null,
    verified_by: doc.verified_by ?? null,
    verified_by_name: profileDisplayName(doc.verified_by_profile),
    categories,
    design_disciplines,
    design_stages,
    linked_issues: [],
  };
}
